/**
 * AIImportUploadStep
 * File-picker step for the AI Import flow. Accepts one or more files
 * (drag/drop or click), uploads them to SharePoint at /AI Imports/{uuid}/,
 * then creates the import job via the submit Edge Function.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { FileUp, X, Loader2, AlertTriangle, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { uploadFile, createFolderByPath, ensureSharePointAuth } from '@/services/sharepoint.service';
import { getMsalInstance } from '@/providers/MsalProvider';
import { useCreateAIImportJob } from '@/hooks/useAIImport';
import type { SubmitJobPayload } from '@/services/ai-import.service';
import {
  canSubmit,
  countQueue,
  filesNeedingUpload,
  queueSummary,
  submitLabel,
  type FailureKind,
  type FileState,
} from './uploadQueue';

interface AIImportUploadStepProps {
  onJobCreated: (jobId: string) => void;
  onCancel: () => void;
  /** Whether to run star-rating analysis on the extracted properties (default OFF; manual opt-in). */
  runStarRating: boolean;
  onRunStarRatingChange: (value: boolean) => void;
}

interface StagedFile {
  id: string;
  file: File;
  state: FileState;
  error?: string;
  /** Set whenever `state` is 'failed' — decides whether a retry can help. */
  failureKind?: FailureKind;
  sharepoint_item_id?: string;
  sharepoint_web_url?: string;
  sharepoint_drive_id?: string;
  // Metadata of what was actually uploaded (may differ from `file` if a large
  // spreadsheet was converted to CSV before upload).
  uploadName?: string;
  uploadSize?: number;
  uploadType?: string;
}

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`;
}

function fileExtension(name: string): string | null {
  const idx = name.lastIndexOf('.');
  if (idx < 0 || idx === name.length - 1) return null;
  return name.slice(idx + 1).toLowerCase();
}

// Large spreadsheets crash the edge worker (SheetJS + ~256MB). The browser has
// far more memory, so convert big Excel files to CSV here — before upload — so
// the worker only ever sees lightweight text. Small spreadsheets keep their
// original format (the worker handles them, and the original is preserved).
const XLSX_CONVERT_MIN_BYTES = 1024 * 1024; // 1 MB

async function maybeConvertSpreadsheet(file: File): Promise<File> {
  if (!/\.(xlsx|xls|xlsm)$/i.test(file.name)) return file;
  if (file.size < XLSX_CONVERT_MIN_BYTES) return file;
  try {
    // SheetJS is ~700 KB and only this conversion needs it, so it is fetched
    // here, on first use, rather than with the app: the AI Import dialog lives
    // in the top bar, so a static import put it on every page load. If the
    // fetch fails, the catch below uploads the original, as for any failure.
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const parts: string[] = [`FILE: ${file.name}`];
    for (const sheetName of wb.SheetNames) {
      const csv = XLSX.utils.sheet_to_csv(wb.Sheets[sheetName], { blankrows: false });
      if (csv && csv.trim()) parts.push(`\n=== SHEET: ${sheetName} ===\n${csv}`);
    }
    const csvName = file.name.replace(/\.(xlsx|xls|xlsm)$/i, '.csv');
    return new File([parts.join('\n')], csvName, { type: 'text/csv' });
  } catch {
    // Conversion failed — upload the original; the worker's size backstop will
    // skip it gracefully rather than crashing.
    return file;
  }
}

// Mirrors AI_IMPORT_MAX_FILE_SIZE_MB on the ai-import-submit Edge Function
// (default 100). Convertible spreadsheets are exempt — they're shrunk to CSV
// before upload, so their on-disk size doesn't matter. Catching oversize files
// here avoids a long upload that the server would only reject at submit time.
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function oversizeError(file: File): string | null {
  if (/\.(xlsx|xls|xlsm)$/i.test(file.name)) return null; // converted to CSV pre-upload
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `Too large (${formatBytes(file.size)}) — ${MAX_FILE_SIZE_MB} MB max per file`;
  }
  return null;
}

// Mirrors the click-picker `accept` list below. Drag-and-drop bypasses `accept`,
// so we re-check the extension here — otherwise an unsupported drop (.pptx/.mov)
// stages happily and only fails minutes later as a processing error.
//
// Only what the worker can read (PDFs, images, spreadsheets, plain text). ZIP and Word
// used to be listed here, and nothing downstream opened either: the worker skipped them
// as unsupported minutes later, and the report called them unread. (An emailed zip IS
// unpacked, by the email poller.)
const ALLOWED_EXTENSIONS = new Set([
  'pdf', 'xlsx', 'xls', 'xlsm', 'csv', 'txt',
  'png', 'jpg', 'jpeg', 'webp', 'eml', 'msg',
]);

// What to do instead, for the types people try and the worker cannot read.
const UNREADABLE_TYPE_ADVICE: Record<string, string> = {
  zip: "ZIP files can't be opened here — unzip it and add the files inside",
  docx: "Word files can't be read — save it as a PDF and add that",
  doc: "Word files can't be read — save it as a PDF and add that",
};

// Advertised in the drop-zone hint ("Up to 50 files"); enforced here so a 60-file
// drop doesn't stage past the server's limit.
const MAX_FILES = 50;

function unsupportedTypeError(file: File): string | null {
  const ext = fileExtension(file.name);
  if (ext && UNREADABLE_TYPE_ADVICE[ext]) return UNREADABLE_TYPE_ADVICE[ext];
  if (!ext || !ALLOWED_EXTENSIONS.has(ext)) {
    return `'.${ext ?? '?'}' isn't supported — PDFs, Excel, images, CSV, or email files only`;
  }
  return null;
}

const UPLOAD_STATE_LABEL: Record<FileState, string> = {
  queued: 'Queued',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  failed: 'Failed',
};

const UPLOAD_STATE_VARIANT: Record<FileState, 'gray' | 'blue' | 'green' | 'red'> = {
  queued: 'gray',
  uploading: 'blue',
  uploaded: 'green',
  failed: 'red',
};

export function AIImportUploadStep({
  onJobCreated,
  onCancel,
  runStarRating,
  onRunStarRatingChange,
}: AIImportUploadStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The SharePoint folder this attempt owns. Held across retries so a second
  // click lands beside the files that already made it, instead of starting a
  // new folder and abandoning the first one.
  const sessionFolderRef = useRef<string | null>(null);
  const [files, setFiles] = useState<StagedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, profile } = useAuthStore();
  const { user: msUser, loginWithRedirect: msLoginRedirect } = useMicrosoftAuth();
  const createJob = useCreateAIImportJob();

  // Microsoft auth gate — checked up front (before files are staged) so a
  // sign-in popup/redirect can never cost the user their staged uploads.
  const [authState, setAuthState] = useState<'checking' | 'needed' | 'ready'>('checking');
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ready = await ensureSharePointAuth(); // silent; no popup if no account
        if (!cancelled) setAuthState(ready ? 'ready' : 'needed');
      } catch {
        if (!cancelled) setAuthState('needed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      // Full-page redirect, NOT a popup. Safari blocks the sign-in popup here:
      // the click gesture is spent by the async nonce hash before MSAL can call
      // window.open, so loginPopup never resolved. The rest of the app (primary
      // login, silent-token fallback) uses redirect for exactly this reason.
      // This gate renders before any file is staged, so navigating away is safe.
      //
      // localStorage (not sessionStorage) so these survive the full-page redirect
      // through Microsoft — the same reason the login nonce uses it. On the way
      // back, MicrosoftCallback consumes the return path and Header consumes the
      // reopen flag to bring the dialog back up. The reopen flag carries a
      // timestamp so a stale, abandoned attempt can't pop the dialog days later.
      localStorage.setItem(
        'flow_post_login_redirect',
        window.location.pathname + window.location.search,
      );
      localStorage.setItem('flow_reopen_ai_import', String(Date.now()));
      await msLoginRedirect();
      // Page navigates to Microsoft; nothing below runs.
    } catch {
      localStorage.removeItem('flow_post_login_redirect');
      localStorage.removeItem('flow_reopen_ai_import');
      setError('Microsoft sign-in could not be started. Please try again.');
      setConnecting(false);
    }
  };

  const addFiles = useCallback((list: FileList | File[]) => {
    const incoming = Array.from(list);
    setFiles((prev) => {
      const staged: StagedFile[] = incoming.map((f, idx) => {
        // Reason precedence: over the count cap, then unsupported type, then
        // oversize. Any reason flags the row 'failed' up front, and marks it
        // 'rejected' below so it blocks submit until the user removes it —
        // no silent server reject.
        const overCap = prev.length + idx >= MAX_FILES;
        const reason = overCap
          ? `Over the ${MAX_FILES}-file limit — remove some files first`
          : unsupportedTypeError(f) ?? oversizeError(f);
        return {
          id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          state: reason ? 'failed' : 'queued',
          error: reason ?? undefined,
          // 'rejected', not 'upload': none of these can succeed on a retry, so
          // they keep blocking submit until the user removes the row.
          failureKind: reason ? ('rejected' as const) : undefined,
        };
      });
      return [...prev, ...staged];
    });
  }, []);

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    // handleSubmit iterates the snapshot it started with, so anything added
    // mid-upload would be left out of the job with no warning.
    if (isSubmitting) return;
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handlePickClick = () => {
    if (isSubmitting) return;
    inputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;
    setError(null);
    setIsSubmitting(true);

    try {
      // Auth was gated up front; re-confirm silently as a safety net.
      const authReady = await ensureSharePointAuth();
      if (!authReady) {
        setAuthState('needed');
        setError('Your Microsoft session expired. Please reconnect and try again.');
        setIsSubmitting(false);
        return;
      }

      // One session folder for the whole attempt *and* every retry of it.
      // Minting a fresh uuid per click re-uploaded files that had already
      // landed and left the previous folder orphaned in SharePoint.
      if (!sessionFolderRef.current) {
        sessionFolderRef.current = `/AI Imports/${crypto.randomUUID()}`;
      }
      const folderPath = sessionFolderRef.current;

      // Ensure SharePoint folder exists. createFolderByPath is idempotent.
      await createFolderByPath(folderPath);

      // Upload each file sequentially, updating state as we go. Already-uploaded
      // files are skipped, so a retry only re-sends what actually failed.
      // Results are keyed by row id and read back in staged order below, so a
      // partial retry can't reshuffle the payload the worker sees.
      const uploaded = new Map<string, StagedFile>(
        files
          .filter((f) => f.state === 'uploaded' && f.sharepoint_item_id)
          .map((f) => [f.id, f]),
      );
      const failures: string[] = [];

      for (const sf of filesNeedingUpload(files)) {
        // A row the user can't fix by retrying (wrong type, oversize) never
        // reaches the network; it just keeps blocking submit.
        if (sf.state === 'failed' && sf.failureKind === 'rejected') continue;

        setFiles((prev) =>
          prev.map((f) =>
            f.id === sf.id
              ? { ...f, state: 'uploading', error: undefined, failureKind: undefined }
              : f,
          ),
        );
        try {
          // Convert large Excel to CSV in-browser so the worker never OOMs on it.
          const fileToUpload = await maybeConvertSpreadsheet(sf.file);
          // 'replace' makes the upload idempotent, which is what lets the
          // service retry a dropped connection without risking a duplicate
          // item — the session folder is ours, so overwriting is always right.
          const result = await uploadFile(fileToUpload, folderPath, {
            conflictBehavior: 'replace',
          });
          const updated: StagedFile = {
            ...sf,
            state: 'uploaded',
            error: undefined,
            failureKind: undefined,
            sharepoint_item_id: result.id,
            sharepoint_web_url: result.webUrl,
            uploadName: fileToUpload.name,
            uploadSize: fileToUpload.size,
            uploadType: fileToUpload.type,
          };
          uploaded.set(sf.id, updated);
          setFiles((prev) => prev.map((f) => (f.id === sf.id ? updated : f)));
        } catch (uploadErr) {
          // Record and carry on. Throwing here used to abandon every file after
          // the first failure — one blip on file 4 of 7 stranded files 5-7 at
          // "Queued" and killed the whole import.
          const message = uploadErr instanceof Error ? uploadErr.message : String(uploadErr);
          failures.push(sf.file.name);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === sf.id
                ? { ...f, state: 'failed', error: message, failureKind: 'upload' }
                : f,
            ),
          );
        }
      }

      if (failures.length > 0) {
        // Everything that uploaded stays uploaded — the retry picks up here.
        setError(
          `${failures.length} of ${files.length} file${files.length === 1 ? '' : 's'} didn't upload ` +
            `(${failures.join(', ')}). The rest are saved — retry to finish, or remove them.`,
        );
        return;
      }

      // Read the MSAL account directly — the useMicrosoftAuth state can be
      // stale within this handler right after a fresh sign-in.
      const msalAccount = getMsalInstance()?.getAllAccounts()[0] ?? null;
      const submittedByEmail =
        user?.email || msUser?.email || msalAccount?.username || null;
      const submittedBy =
        profile?.full_name || msUser?.name || msalAccount?.name || submittedByEmail || 'Unknown';

      const payload: SubmitJobPayload = {
        source: 'upload',
        submitted_by: submittedBy,
        submitted_by_email: submittedByEmail,
        // Staged order, not upload order — a partial retry uploads out of
        // sequence and the worker's per-file property targeting reads position.
        files: files.flatMap((f) => {
          const done = uploaded.get(f.id);
          if (!done) return [];
          return [
            {
              file_name: done.uploadName ?? done.file.name,
              file_size: done.uploadSize ?? done.file.size,
              file_type: done.uploadType || done.file.type || undefined,
              file_extension: fileExtension(done.uploadName ?? done.file.name) ?? undefined,
              sharepoint_item_id: done.sharepoint_item_id,
              sharepoint_web_url: done.sharepoint_web_url,
              source: 'dashboard_upload' as const,
            },
          ];
        }),
      };

      const result = await createJob.mutateAsync(payload);
      // The job owns these files now; a later import starts a fresh folder.
      sessionFolderRef.current = null;
      onJobCreated(result.job_id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const counts = countQueue(files);
  const submitEnabled = canSubmit(counts);

  // Auth gate — render before the file picker so sign-in can't lose staged files
  if (authState === 'checking') {
    return (
      <div className="p-10 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">Checking your Microsoft connection…</div>
      </div>
    );
  }

  if (authState === 'needed') {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary-50 flex items-center justify-center dark:bg-primary-900/30">
          <ShieldCheck className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Connect Microsoft to continue</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto dark:text-gray-400">
            AI Import stores your files in SharePoint, which needs a Microsoft sign-in. Connect once
            and the file picker will appear — your uploads won't be interrupted.
          </p>
        </div>
        {error && (
          <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}
        <div className="flex justify-center gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel} disabled={connecting}>Cancel</Button>
          <Button onClick={handleConnect} isLoading={connecting} leftIcon={<ShieldCheck className="h-4 w-4" />}>
            Sign in with Microsoft
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload files — click or press Enter to browse, or drag and drop"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handlePickClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePickClick();
          }
        }}
        aria-disabled={isSubmitting}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          dragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
            : 'border-gray-300 hover:border-primary-400 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-800',
          isSubmitting && 'cursor-not-allowed opacity-60',
        )}
      >
        <FileUp className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
        <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium text-primary-700 dark:text-primary-300">Click to upload</span>
          {' '}or drag and drop
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          PDFs, Excel, CSV, images. Up to 50 files, 100 MB each. Large Excel files are
          converted to CSV for analysis. Unzip ZIP files first, and save Word files as PDF.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          accept=".pdf,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp,.eml,.msg"
        />
      </div>

      {/* Staged file list */}
      {files.length > 0 && (
        <div className="border rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <FileUp className="h-4 w-4 text-gray-400 flex-shrink-0 dark:text-gray-500" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm text-gray-900 truncate dark:text-gray-100">{f.file.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatBytes(f.file.size)}
                    {f.error && (
                      <span className="text-red-600 dark:text-red-400"> · {f.error}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge size="sm" variant={UPLOAD_STATE_VARIANT[f.state]}>
                  {f.state === 'uploading' && <Loader2 className="h-3 w-3 animate-spin" />}
                  {UPLOAD_STATE_LABEL[f.state]}
                </Badge>
                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:text-gray-500 dark:hover:text-gray-400"
                  aria-label={`Remove ${f.file.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Star-rating opt-in */}
      <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start gap-2 min-w-0">
          <Sparkles className="h-4 w-4 flex-shrink-0 text-accent-500 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Run star-rating analysis (optional)</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Off by default — rating runs only when you turn it on, or from the “Re-rate” button
              on a property. Rates each community's physical quality (0–5★). MHC only.
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={runStarRating}
          aria-label="Run star-rating analysis"
          disabled={isSubmitting}
          onClick={() => onRunStarRatingChange(!runStarRating)}
          className={cn(
            'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors mt-0.5',
            runStarRating ? 'bg-accent-500' : 'bg-gray-300 dark:bg-gray-700',
            isSubmitting && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
              runStarRating ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>

      {/* Top-level error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {queueSummary(
            counts,
            formatBytes(files.reduce((sum, f) => sum + (f.uploadSize ?? f.file.size), 0)),
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!submitEnabled || isSubmitting}
            isLoading={isSubmitting}
          >
            {submitLabel(counts)}
          </Button>
        </div>
      </div>
    </div>
  );
}
